import { Link } from "react-router-dom";
import { AlertTriangle, Bookmark, ShoppingCart, Trash2 } from "lucide-react";
import type { CartLine } from "@/api/types";
import { errorMessage } from "@/api/errors";
import { QuantityStepper } from "@/components/commerce/QuantityStepper";
import { SmartImage } from "@/components/ui/SmartImage";
import { formatNaira } from "@/lib/money";
import { toast } from "@/stores/toast";
import { useRemoveCartItem, useUpdateCartItem } from "./api";

function optionsText(line: CartLine): string {
  return line.variant ? Object.values(line.variant.options).join(" · ") : "";
}

/** One cart line with stock/price warnings, quantity and save-for-later. */
export function CartLineRow({ line, compact = false }: { line: CartLine; compact?: boolean }) {
  const update = useUpdateCartItem();
  const remove = useRemoveCartItem();
  const busy = update.isPending || remove.isPending;
  const onError = (e: unknown) => toast.error(errorMessage(e));
  const problem = line.unavailable
    ? "No longer available"
    : line.out_of_stock
      ? "Out of stock"
      : line.insufficient_stock
        ? `Only ${line.stock} left`
        : null;

  return (
    <li className="flex gap-3 py-4">
      {/* Same destination as the title link below, so it stays out of the tab order and the accessibility tree. */}
      <Link to={`/p/${line.product.slug}`} tabIndex={-1} aria-hidden className="size-20 shrink-0 overflow-hidden rounded-input bg-fg/5">
        <SmartImage src={line.product.cover?.url} alt="" width={160} maxWidth={320} sizes="80px" compact className="size-full" />
      </Link>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link to={`/p/${line.product.slug}`} className="line-clamp-2 text-sm font-medium text-ink hover:text-[var(--primary-text)]">
          {line.product.title}
        </Link>
        {line.variant && <p className="text-xs text-ink-muted">{optionsText(line)}</p>}
        <p className="tabular flex flex-wrap items-center gap-x-2 text-sm font-semibold text-ink">
          {formatNaira(line.unit_price)}
          {line.price_source !== "BASE" && line.list_price > line.unit_price && (
            <span className="text-xs font-normal text-ink-muted line-through">{formatNaira(line.list_price)}</span>
          )}
          {line.price_source === "OFFER" && (
            <span className="rounded-full bg-success/15 px-2 py-0.5 text-[11px] font-medium text-success">Your offer</span>
          )}
          {line.price_source === "DROP" && (
            <span className="rounded-full bg-magenta/15 px-2 py-0.5 text-[11px] font-medium text-magenta">Reality Drop</span>
          )}
          {line.price_changed && (
            <span className={`ml-2 text-xs font-normal ${line.unit_price > line.previous_price ? "text-warning" : "text-success"}`}>
              was {formatNaira(line.previous_price)}
            </span>
          )}
        </p>
        {line.price_note && line.price_source === "BASE" && <p className="text-xs text-warning">{line.price_note}</p>}
        {problem && (
          <p className="flex items-center gap-1 text-xs text-danger" role="status">
            <AlertTriangle className="size-3.5" aria-hidden /> {problem}
          </p>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {!line.saved_for_later && !line.unavailable && !line.out_of_stock && (
            <QuantityStepper
              value={line.quantity}
              max={Math.max(Math.min(line.stock, 99), 1)}
              disabled={busy}
              label={`Quantity of ${line.product.title}`}
              onChange={(quantity) => update.mutate({ id: line.id, quantity }, { onError })}
            />
          )}
          {!compact && (
            <button
              type="button"
              disabled={busy}
              onClick={() => update.mutate({ id: line.id, saved_for_later: !line.saved_for_later }, { onError })}
              className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs text-ink-soft hover:bg-fg/5 hover:text-ink"
            >
              {line.saved_for_later ? <ShoppingCart className="size-3.5" aria-hidden /> : <Bookmark className="size-3.5" aria-hidden />}
              {line.saved_for_later ? "Move to cart" : "Save for later"}
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => remove.mutate(line.id, { onError })}
            className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs text-ink-soft hover:bg-danger/10 hover:text-danger"
            aria-label={`Remove ${line.product.title}`}
          >
            <Trash2 className="size-3.5" aria-hidden /> {compact ? "" : "Remove"}
          </button>
        </div>
      </div>
      {!compact && <p className="tabular hidden shrink-0 text-sm font-semibold sm:block">{formatNaira(line.line_total)}</p>}
    </li>
  );
}
